import * as THREE from "three";
import debug from "debug";

const log = debug("app:transparency");

/**
 * Class to manage transparency changes in a 3D scene for objects in specific layers.
 * Does not emit "xmd" so that opacity-only changes do not trigger full scene recalc (BOM, objectIdMap, etc.).
 * @category Experience
 * @subcategory Manipulation
 */
class Transparency {
  /**
   * Constructs a Transparency instance.
   * @param {Object} experience - The main application experience object.
   * @param {THREE.Scene} experience.scene - The 3D scene containing objects.
   * @param {Object} experience.eventHub - The event hub for managing custom events.
   */
  constructor(experience) {
    this.scene = experience.scene;
    this.eventHub = experience.eventHub;
    this._rafId = null;
    this._pending = null; // Map<layer, value> to coalesce rapid slider input (e.g. Housing emits 4 layers)
    this._onSliderChangeBound = this._onSliderChange.bind(this);
    this.is3DModeActive = false;
    this._on3dEnabledBound = (e) => {
      this.is3DModeActive = !!e.enabled;
    };
    this.init();
  }

  /**
   * Initializes the Transparency instance and sets up event listeners.
   */
  init() {
    this.eventHub.on("sliderChange", this._onSliderChangeBound);
    this.eventHub.on("3d-enabled", this._on3dEnabledBound);
  }

  /**
   * Throttles slider changes to one apply per frame to avoid lag while dragging.
   * Batches multiple layer updates (e.g. Housing → 20, 37, 60, 90) into a single traverse.
   */
  _onSliderChange(e) {
    log("event", e);
    if (!this._pending) this._pending = new Map();
    this._pending.set(e.layer, e.value);
    if (this._rafId !== null) return;
    this._rafId = requestAnimationFrame(() => {
      this._rafId = null;
      const pending = this._pending;
      this._pending = null;
      if (pending && this.scene) {
        pending.forEach((value, layer) => this.changeTransparency(layer, value));
        this.eventHub.emit("needsRender", "true");
      }
    });
  }

  cleanup() {
    log("Cleaning up Transparency...");
    if (this._rafId !== null) {
      cancelAnimationFrame(this._rafId);
      this._rafId = null;
    }
    this._pending = null;
    this.eventHub.off("sliderChange", this._onSliderChangeBound);
    this.eventHub.off("3d-enabled", this._on3dEnabledBound);
    this.scene = null;
    this.eventHub = null;
    log("Transparency cleanup complete.");
  }

  /**
   * Sets opacity on a mesh (single or array of materials) or recurses into groups.
   * Used so 3D mode works when "Shape" is a Group of meshes (e.g. from GLB).
   * In 3D only: treat opacity >= 0.99 as opaque so depth pass writes them and edges draw on these blocks.
   * In 2D: use strict opacity < 1 so built-in transparency of 2D assets is preserved.
   * @param {THREE.Object3D} object - Mesh or Group.
   * @param {number} opacityValue - Opacity 0–1.
   */
  _setOpacityOnNode(object, opacityValue) {
    if (object.isMesh && object.material) {
      const materials = Array.isArray(object.material)
        ? object.material
        : [object.material];
      const isTransparent = this.is3DModeActive
        ? opacityValue < 0.99
        : opacityValue < 1;
      materials.forEach((mat) => {
        if (!mat) return;
        mat.opacity = opacityValue;
        mat.transparent = isTransparent;
        mat.needsUpdate = true;
        if (isTransparent) {
          mat.depthWrite = false;
          mat.alphaTest = 0.01;
        } else {
          mat.depthWrite = true;
          mat.alphaTest = 0;
        }
      });
    }
    if (object.children && object.children.length) {
      object.children.forEach((child) => this._setOpacityOnNode(child, opacityValue));
    }
  }

  /**
   * Changes the transparency of objects in the specified layer based on the given opacity value.
   * @param {number} layer - The layer identifier to filter objects for transparency adjustment.
   * @param {number} opacityValue - The opacity value to apply (0 for fully transparent, 1 for fully opaque).
   */
  changeTransparency(layer, opacityValue) {
    this.scene.traverse((object) => {
      if (object.type === "Group" && object.name === "XMO") {
        log(object, "Processing XMO Group");

        if (object.userData.layer == layer) {
          log(`XMO Group matched layer ${layer}`);

          object.children.forEach((child) => {
            if (child.name === "bBox" && opacityValue > 0) {
              log("Skipping bBox from transparency changes.");
              return;
            }

            if (child.isMesh || child.isObject3D) {
              this._setOpacityOnNode(child, opacityValue);

              if (opacityValue <= 0) {
                if (child.isMesh) {
                  child.raycast = function () {};
                }
                log(`Setting ${child.name} to invisible due to opacity 0.`);
              } else {
                if (child.isMesh) {
                  child.raycast = THREE.Mesh.prototype.raycast;
                }
                log(
                  `Setting ${child.name} to visible with opacity ${opacityValue}.`
                );
              }
            }
          });

          object.visible = opacityValue > 0;
          // Do not emit "xmd" — opacity is view-only and would trigger full recalc (BOM, objectIdMap, etc.)
          log(
            `XMO Group and its children updated. Layer: ${layer}, Opacity: ${opacityValue}, Visible: ${object.visible}`
          );
        } else {
          log(`XMO Group does not match the layer ${layer}`);
        }
      }
    });
  }
}

export default Transparency;
